import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { TodayResponse } from '../lib/api';
import { todayQueryOptions, recapYearQueryOptions } from '../lib/queries';
import { useAuth } from '../lib/auth';
import { ScreenHeader } from '../components/ScreenHeader';
import { Button } from '../components/Button';

export default function TodayPage() {
  const auth = useAuth();
  const me = auth.state === 'signed-in' ? auth.me : null;
  const today = useQuery(todayQueryOptions());

  if (today.isLoading) {
    return <FullPageLoading />;
  }
  if (today.error || !today.data) {
    return (
      <>
        <ScreenHeader title="Today" />
        <div className="px-5 pt-5">
          <p className="text-soft">
            No devotion is published for today yet. New entries arrive each morning.
          </p>
        </div>
      </>
    );
  }

  const t = today.data;
  const dateLabel = new Date(t.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <ScreenHeader
        title="Today"
        right={<span className="text-soft text-sm pb-1">{dateLabel}</span>}
      />
      <div className="px-5 pt-4 pb-6">
        <RecapCard />
        <VerseCard verse={t.verse} />
        <PrincipleCard text={t.verse.principle} />
        <TryCard verse={t.verse} role={me?.member.role ?? 'parent'} />
        {t.fotf && <FotfCard fotf={t.fotf} />}
        <DiscussionCard text={t.verse.discussion} />
      </div>
    </>
  );
}

function RecapCard() {
  // Annual recap surfaces on /today only in December. The query is
  // skipped the rest of the year so we don't ping the recap endpoint
  // every Today open.
  const now = new Date();
  const isDecember = now.getMonth() === 11;
  const year = now.getFullYear();
  const recap = useQuery({
    ...recapYearQueryOptions(year),
    enabled: isDecember,
  });
  if (!isDecember) return null;
  if (!recap.data) return null;
  // Only show the card if the family has *something* to recap. The
  // empty-state copy already lives on /recap; no need to advertise it.
  const hasContent =
    recap.data.photoCount +
      recap.data.prayerCount +
      recap.data.journalEntries +
      recap.data.gratitudeEntries +
      recap.data.memoryVerseWeeksCompleted +
      recap.data.readingPlanDaysCompleted >
    0;
  if (!hasContent) return null;
  return (
    <Link
      to={`/recap/${year}`}
      className="card p-5 mb-4 block bg-sage/10 hover:bg-sage/15 transition-colors"
    >
      <div className="kicker text-sage">Year in review</div>
      <div className="font-serif text-ink text-xl mt-1">Your {year} recap is ready →</div>
      <p className="text-soft text-sm mt-2">
        {recap.data.photoCount} photos · {recap.data.prayerCount} prayers ·{' '}
        {recap.data.journalEntries + recap.data.gratitudeEntries} entries
      </p>
    </Link>
  );
}

function VerseCard({ verse }: { verse: TodayResponse['verse'] }) {
  return (
    <article className="card p-6 mb-4">
      <div className="font-serif italic text-oxblood text-xl">{verse.ref}</div>
      <p className="verse-body text-2xl text-ink mt-3">{verse.body}</p>
      {verse.audioUrl && (
        <Button
          variant="secondary"
          size="sm"
          className="mt-5"
          onClick={() => {
            const a = new Audio(verse.audioUrl!);
            void a.play();
          }}
        >
          <span className="inline-block w-0 h-0 border-y-[6px] border-y-transparent border-l-[10px] border-l-oxblood" />
          Listen
        </Button>
      )}
    </article>
  );
}

function PrincipleCard({ text }: { text: string }) {
  return (
    <article className="card p-5 mb-4">
      <div className="font-serif text-sage text-xl">Principle</div>
      <p className="text-ink mt-2 leading-relaxed">{text}</p>
    </article>
  );
}

function TryCard({
  verse,
  role,
}: {
  verse: TodayResponse['verse'];
  role: 'parent' | 'youth';
}) {
  const [tier, setTier] = useState<'adult' | 'youth'>(
    role === 'youth' ? 'youth' : 'adult',
  );
  const text = tier === 'adult' ? verse.applicationAdult : verse.applicationYouth;
  return (
    <article className="card p-5 mb-4">
      <div className="font-serif text-oxblood text-xl">This Week, Try</div>
      <div className="mt-3 inline-flex rounded-full border border-line p-1 bg-paper">
        <TierPill active={tier === 'adult'} onClick={() => setTier('adult')}>
          Adult
        </TierPill>
        <TierPill active={tier === 'youth'} onClick={() => setTier('youth')}>
          Youth
        </TierPill>
      </div>
      <p className="text-soft mt-3 leading-relaxed">{text}</p>
    </article>
  );
}

function TierPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 h-8 rounded-full text-sm font-medium ${
        active ? 'bg-ink text-paper' : 'text-muted'
      }`}
    >
      {children}
    </button>
  );
}

function youtubeVideoId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1) || null;
    if (u.hostname.endsWith('youtube.com')) {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      if (u.pathname.startsWith('/embed/')) return u.pathname.slice(7) || null;
      if (u.pathname.startsWith('/shorts/')) return u.pathname.slice(8) || null;
    }
    return null;
  } catch {
    return null;
  }
}

function FotfCard({ fotf }: { fotf: NonNullable<TodayResponse['fotf']> }) {
  const ytId = fotf.url ? youtubeVideoId(fotf.url) : null;
  const isMedia =
    !!fotf.url && /\.(mp3|m4a|aac|ogg|wav|mp4|m4v|mov)(\?|#|$)/i.test(fotf.url);

  // YouTube: render the embed inline so the user sees the poster + YT's
  // native play-button overlay, no extra "click to reveal" step.
  // loading="lazy" so the iframe doesn't fire a request until the card
  // is near the viewport.
  if (ytId) {
    return (
      <article className="card mb-4 overflow-hidden">
        <div className="p-5 pb-4">
          <div className="kicker text-sage">Daily Devotional Video</div>
          <h3 className="font-serif text-xl mt-1 text-ink leading-snug">{fotf.title}</h3>
        </div>
        <div className="aspect-video w-full bg-ink">
          <iframe
            src={`https://www.youtube.com/embed/${ytId}?rel=0&playsinline=1`}
            title={fotf.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            loading="lazy"
          />
        </div>
      </article>
    );
  }

  // Non-YouTube fallback: audio file (in-app playback) or external URL
  // (open in browser). Same play-button-card UI as before.
  const info = (
    <>
      <div className="flex-1">
        <div className="kicker text-sage">{fotf.kicker}</div>
        <h3 className="font-serif text-xl mt-1 text-ink leading-snug">{fotf.title}</h3>
        <div className="text-muted text-sm mt-1">
          {[fotf.duration, fotf.host].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div
        aria-hidden
        className="w-14 h-14 rounded-full border-2 border-oxblood flex items-center justify-center shrink-0"
      >
        <span className="inline-block w-0 h-0 border-y-[8px] border-y-transparent border-l-[12px] border-l-oxblood" />
      </div>
    </>
  );

  if (!fotf.url) {
    return <article className="card p-5 mb-4 flex items-center gap-4">{info}</article>;
  }

  return (
    <button
      type="button"
      onClick={() => {
        if (isMedia) {
          const a = new Audio(fotf.url!);
          void a.play();
          return;
        }
        // iOS PWAs in standalone mode silently drop `target="_blank"` —
        // there are no tabs. window.open + a same-window fallback is the
        // most reliable way to hand the URL to Safari.
        const win = window.open(fotf.url!, '_blank', 'noopener,noreferrer');
        if (!win || win.closed) {
          window.location.href = fotf.url!;
        }
      }}
      className="card p-5 mb-4 flex items-center gap-4 active:bg-line/40 transition-colors w-full text-left"
      aria-label={`${isMedia ? 'Play' : 'Open'}: ${fotf.title}`}
    >
      {info}
    </button>
  );
}

function DiscussionCard({ text }: { text: string }) {
  return (
    <article className="card p-5">
      <div className="kicker text-soft">Family Discussion</div>
      <p className="font-serif italic text-ink text-xl mt-2 leading-snug">{text}</p>
    </article>
  );
}

function FullPageLoading() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-soft text-sm">Loading…</div>
    </div>
  );
}
